// components/Modals/InstagramProfileModal/InstagramProfileModal.js
import React, { useState, useRef } from 'react';
import { toast } from 'react-hot-toast';
import { instagramScrapeAPI } from '../../../services/api';
import { API_URL } from '../../../config';
import PlatformSettingsPanel from '../../Shared/PlatformSettingsPanel/PlatformSettingsPanel';
import './InstagramProfileModal.css';

const InstagramProfileModal = ({ isOpen, onClose, tags, onSuccess }) => {
  const [step, setStep] = useState(1);
  const [url, setUrl] = useState('');
  const [profileData, setProfileData] = useState(null);
  const [selectedTags, setSelectedTags] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [importType, setImportType] = useState('posts'); // 'posts' | 'highlights' | 'both'

  // Step 3: Post çekme ilerleme
  const [fetchedPages, setFetchedPages] = useState(0);
  const [fetchedPosts, setFetchedPosts] = useState(0);
  const [fetchPhase, setFetchPhase] = useState('');
  const [fetchHighlightIndex, setFetchHighlightIndex] = useState(0);
  const [fetchHighlightTotal, setFetchHighlightTotal] = useState(0);
  const [fetchHighlightTitle, setFetchHighlightTitle] = useState('');
  const [fetchedHighlightMedia, setFetchedHighlightMedia] = useState(0);

  // Step 3-4: Postlar ve highlights
  const [allPosts, setAllPosts] = useState([]);
  const [allHighlightItems, setAllHighlightItems] = useState([]);
  const [totalMedia, setTotalMedia] = useState(0);
  const [imageCount, setImageCount] = useState(0);
  const [videoCount, setVideoCount] = useState(0);

  // Step 4: İndirme ilerleme
  const [currentPostIndex, setCurrentPostIndex] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [savedCount, setSavedCount] = useState(0);
  const [errorCount, setErrorCount] = useState(0);
  const [downloadedMedia, setDownloadedMedia] = useState(0);
  const [isCompleted, setIsCompleted] = useState(false);
  const [downloadPhase, setDownloadPhase] = useState(''); // 'posts' | 'highlights'
  const cancelRef = useRef(false);

  // ─── Platform Ayarları State'leri ───────────────────
  const [contentText, setContentText] = useState('');
  const [buttonForm, setButtonForm] = useState([]);
  const [youtubeSettings, setYoutubeSettings] = useState({
    title: '', description: '', categoryId: '24'
  });
  const [xSettings, setXSettings] = useState({ text: '' });
  const [tiktokSettings, setTiktokSettings] = useState({
    caption: '', hashtags: '', allowComments: true, allowDuet: true
  });
  const [instagramSettings, setInstagramSettings] = useState({
    contentType: 'post', caption: '', firstComment: '', collaborators: '', userTags: ''
  });
  const [showPlatformSettings, setShowPlatformSettings] = useState(false);

  const resetModal = () => {
    setStep(1);
    setUrl('');
    setProfileData(null);
    setSelectedTags([]);
    setIsLoading(false);
    setImportType('posts');
    setFetchedPages(0);
    setFetchedPosts(0);
    setFetchPhase('');
    setFetchHighlightIndex(0);
    setFetchHighlightTotal(0);
    setFetchHighlightTitle('');
    setFetchedHighlightMedia(0);
    setAllPosts([]);
    setAllHighlightItems([]);
    setTotalMedia(0);
    setImageCount(0);
    setVideoCount(0);
    setCurrentPostIndex(0);
    setTotalItems(0);
    setSavedCount(0);
    setErrorCount(0);
    setDownloadedMedia(0);
    setIsCompleted(false);
    setDownloadPhase('');
    cancelRef.current = false;
    // Platform ayarları reset
    setContentText('');
    setButtonForm([]);
    setYoutubeSettings({ title: '', description: '', categoryId: '24' });
    setXSettings({ text: '' });
    setTiktokSettings({ caption: '', hashtags: '', allowComments: true, allowDuet: true });
    setInstagramSettings({ contentType: 'post', caption: '', firstComment: '', collaborators: '', userTags: '' });
    setShowPlatformSettings(false);
  };

  const handleClose = () => {
    if ((step === 3 || step === 4) && !isCompleted) {
      if (!window.confirm('İşlem devam ediyor. İptal etmek istiyor musunuz?')) {
        return;
      }
      cancelRef.current = true;
    }
    resetModal();
    onClose();
  };

  const handlePreview = async () => {
    const input = url.trim();
    if (!input) {
      toast.error('Instagram profil URL veya kullanıcı adı girin');
      return;
    }

    setIsLoading(true);
    try {
      const response = await instagramScrapeAPI.profilePreview(input);
      if (response.data.success) {
        setProfileData(response.data.data);
        setStep(2);
      } else {
        toast.error(response.data.error || 'Profil bilgisi alınamadı');
      }
    } catch (error) {
      const msg = error.response?.data?.error || 'Profil bilgisi alınamadı';
      toast.error(msg);
    } finally {
      setIsLoading(false);
    }
  };

  // platformConfig objesi oluştur
  const buildPlatformConfig = () => {
    const pc = {};

    // Telegram
    if (contentText) pc.content = contentText;
    if (buttonForm && buttonForm.length > 0) pc.buttons = buttonForm;

    // YouTube
    if (youtubeSettings.title) pc.youtubeTitle = youtubeSettings.title;
    if (youtubeSettings.description) pc.youtubeDescription = youtubeSettings.description;
    if (youtubeSettings.categoryId) pc.youtubeCategoryId = youtubeSettings.categoryId;
    pc.isYoutubeShort = true; // profil çekmede genelde kısa videolar

    // X
    if (xSettings.text) pc.xText = xSettings.text;

    // TikTok
    if (tiktokSettings.caption) pc.tiktokCaption = tiktokSettings.caption;
    if (tiktokSettings.hashtags) pc.tiktokHashtags = tiktokSettings.hashtags;
    pc.tiktokAllowComments = tiktokSettings.allowComments !== false;
    pc.tiktokAllowDuet = tiktokSettings.allowDuet !== false;
    pc.tiktokEnabled = !!(tiktokSettings.caption || tiktokSettings.hashtags);

    // Instagram
    if (instagramSettings.caption) pc.instagramCaption = instagramSettings.caption;
    pc.instagramContentType = instagramSettings.contentType || 'post';
    if (instagramSettings.firstComment) pc.instagramFirstComment = instagramSettings.firstComment;
    if (instagramSettings.collaborators) pc.instagramCollaborators = instagramSettings.collaborators;
    if (instagramSettings.userTags) pc.instagramUserTags = instagramSettings.userTags;
    pc.instagramEnabled = !!(instagramSettings.caption || instagramSettings.firstComment);

    return Object.keys(pc).length > 0 ? pc : null;
  };

  // Step 2 → Step 3: İçerik çekmeye başla
  const handleStartImport = async () => {
    if (selectedTags.length === 0) {
      toast.error('En az bir etiket seçmelisiniz');
      return;
    }

    setStep(3);
    cancelRef.current = false;

    let posts = [];
    let highlightItems = [];
    let totalM = 0, imgC = 0, vidC = 0;

    // Postları çek
    if (importType === 'posts' || importType === 'both') {
      setFetchPhase('posts');
      posts = await fetchAllPosts();
      if (cancelRef.current) return;
    }

    // Highlights çek
    if (importType === 'highlights' || importType === 'both') {
      setFetchPhase('highlights');
      highlightItems = await fetchAllHighlights();
      if (cancelRef.current) return;
    }

    // Medya sayılarını hesapla
    posts.forEach(p => {
      p.mediaItems.forEach(m => {
        totalM++;
        if (m.type === 'image') imgC++;
        if (m.type === 'video') vidC++;
      });
    });
    highlightItems.forEach(item => {
      totalM++;
      if (item.type === 'image') imgC++;
      if (item.type === 'video') vidC++;
    });

    setAllPosts(posts);
    setAllHighlightItems(highlightItems);
    setTotalMedia(totalM);
    setImageCount(imgC);
    setVideoCount(vidC);

    if (totalM === 0) {
      toast.error('İndirilecek medya bulunamadı');
      setStep(2);
      return;
    }

    // İndirmeye başla
    await startDownloading(posts, highlightItems, totalM);
  };

  // Post listesini sayfa sayfa çek
  const fetchAllPosts = async () => {
    setFetchedPages(0);
    setFetchedPosts(0);

    let collectedPosts = [];
    let maxId = null;
    let pageNum = 0;
    let moreAvailable = true;

    try {
      while (moreAvailable) {
        if (cancelRef.current) return [];

        pageNum++;
        const response = await instagramScrapeAPI.profilePosts(url.trim(), maxId);

        if (!response.data.success) {
          toast.error(response.data.error || 'Postlar çekilemedi');
          return collectedPosts;
        }

        const data = response.data.data;
        collectedPosts = collectedPosts.concat(data.posts);
        moreAvailable = data.moreAvailable;
        maxId = data.nextMaxId;

        setFetchedPages(pageNum);
        setFetchedPosts(collectedPosts.length);
      }
    } catch (error) {
      toast.error('Postlar çekilirken hata oluştu');
    }

    return collectedPosts;
  };

  // Highlight'ları çek
  const fetchAllHighlights = async () => {
    setFetchHighlightIndex(0);
    setFetchHighlightTotal(0);
    setFetchedHighlightMedia(0);

    try {
      const response = await instagramScrapeAPI.profileHighlights(url.trim());

      if (!response.data.success) {
        toast.error(response.data.error || 'Öne çıkanlar çekilemedi');
        return [];
      }

      const data = response.data.data;
      const highlights = data.highlights || [];

      setFetchHighlightTotal(highlights.length);

      let allItems = [];
      if (data.allItems) {
        allItems = data.allItems;
        setFetchHighlightIndex(highlights.length);
        setFetchedHighlightMedia(allItems.length);
      }

      return allItems;
    } catch (error) {
      toast.error('Öne çıkanlar çekilirken hata oluştu');
      return [];
    }
  };

  // İndirme akışı
  const startDownloading = async (posts, highlightItems, totalMediaCount) => {
    setStep(4);
    const totalItemCount = posts.length + highlightItems.length;
    setTotalItems(totalItemCount);

    const platformConfig = buildPlatformConfig();

    let saved = 0;
    let errors = 0;
    let downloaded = 0;
    let itemIndex = 0;

    // 1. Postları indir
    if (posts.length > 0) {
      setDownloadPhase('posts');

      for (let i = 0; i < posts.length; i++) {
        if (cancelRef.current) break;

        const post = posts[i];
        itemIndex++;
        setCurrentPostIndex(itemIndex);

        if (!post.mediaItems || post.mediaItems.length === 0) continue;

        try {
          const response = await instagramScrapeAPI.profileDownloadPost(
            post, selectedTags, profileData.username, saved + 1, platformConfig
          );
          if (response.data.success) {
            saved += response.data.data.savedCount;
            errors += response.data.data.errorCount;
            downloaded += post.mediaItems.length;
            setSavedCount(saved);
            setErrorCount(errors);
            setDownloadedMedia(downloaded);
          }
        } catch (error) {
          errors += post.mediaItems.length;
          downloaded += post.mediaItems.length;
          setErrorCount(errors);
          setDownloadedMedia(downloaded);
        }
      }
    }

    // 2. Highlight medyalarını indir
    if (highlightItems.length > 0 && !cancelRef.current) {
      setDownloadPhase('highlights');

      for (let i = 0; i < highlightItems.length; i++) {
        if (cancelRef.current) break;

        const item = highlightItems[i];
        itemIndex++;
        setCurrentPostIndex(itemIndex);

        try {
          const response = await instagramScrapeAPI.profileDownloadHighlight(
            item, selectedTags, profileData.username, saved + 1, platformConfig
          );
          if (response.data.success && response.data.data.success) {
            saved++;
            downloaded++;
            setSavedCount(saved);
            setDownloadedMedia(downloaded);
          } else {
            errors++;
            downloaded++;
            setErrorCount(errors);
            setDownloadedMedia(downloaded);
          }
        } catch (error) {
          errors++;
          downloaded++;
          setErrorCount(errors);
          setDownloadedMedia(downloaded);
        }
      }
    }

    setIsCompleted(true);
    setDownloadPhase('completed');

    if (saved > 0) {
      toast.success(`${saved} medya kütüphaneye eklendi!`, { duration: 5000 });
    }
  };

  const toggleTag = (tagId) => {
    setSelectedTags(prev =>
      prev.includes(tagId)
        ? prev.filter(id => id !== tagId)
        : [...prev, tagId]
    );
  };

  const formatNumber = (num) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return String(num);
  };

  const getProxyImageUrl = (originalUrl) => {
    if (!originalUrl) return '';
    return `${API_URL}/instagramscrape/profile/proxy-image?url=${encodeURIComponent(originalUrl)}`;
  };

  const getProgressPercent = () => {
    if (totalMedia === 0) return 0;
    return Math.round((downloadedMedia / totalMedia) * 100);
  };

  const getFetchPercent = () => {
    if (!profileData || !profileData.postCount) return 0;
    return Math.round((fetchedPosts / profileData.postCount) * 100);
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) handleClose(); }}>
      <div className="instagram-profile-modal">

        {/* Header */}
        <div className="ipm-header">
          <h3>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" style={{ marginRight: '8px', verticalAlign: 'middle' }}>
              <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073z"/>
              <circle cx="12" cy="12" r="3.2"/>
            </svg>
            Instagram Profil Cek
          </h3>
          <div className="ipm-steps">
            <span className={`ipm-step ${step >= 1 ? 'active' : ''}`}>1. Profil</span>
            <span className="ipm-step-arrow">→</span>
            <span className={`ipm-step ${step >= 2 ? 'active' : ''}`}>2. Ayarlar</span>
            <span className="ipm-step-arrow">→</span>
            <span className={`ipm-step ${step >= 3 ? 'active' : ''}`}>3. Cek</span>
            <span className="ipm-step-arrow">→</span>
            <span className={`ipm-step ${step >= 4 ? 'active' : ''}`}>4. Indir</span>
          </div>
          <button className="ipm-close" onClick={handleClose}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>

        {/* Body */}
        <div className="ipm-body">

          {/* Step 1: URL Giriş */}
          {step === 1 && (
            <div className="ipm-step-content">
              <div className="ipm-url-section">
                <label>Instagram Profil URL veya Kullanici Adi</label>
                <div className="ipm-url-input-wrapper">
                  <input
                    type="text"
                    value={url}
                    onChange={e => setUrl(e.target.value)}
                    placeholder="https://www.instagram.com/username veya @username"
                    className="ipm-url-input"
                    onKeyDown={e => e.key === 'Enter' && !isLoading && handlePreview()}
                    autoFocus
                  />
                  <button
                    onClick={handlePreview}
                    disabled={isLoading || !url.trim()}
                    className="ipm-btn-primary"
                  >
                    {isLoading ? 'Cekiliyor...' : 'Profili Getir'}
                  </button>
                </div>
                <p className="ipm-hint">Profildeki postlar ve one cikan hikayeleri cekebilirsiniz</p>
              </div>
            </div>
          )}

          {/* Step 2: Profil + Seçenekler + Etiket + Platform Ayarları */}
          {step === 2 && profileData && (
            <div className="ipm-step-content">
              {/* Profil Kartı */}
              <div className="ipm-profile-card">
                <div className="ipm-profile-header">
                  <img
                    src={getProxyImageUrl(profileData.profilePicUrl)}
                    alt={profileData.username}
                    className="ipm-profile-pic"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                  <div className="ipm-profile-info">
                    <div className="ipm-profile-name">
                      <h4>{profileData.fullName || profileData.username}</h4>
                      {profileData.isVerified && <span className="ipm-verified">✓</span>}
                    </div>
                    <span className="ipm-username">@{profileData.username}</span>
                    {profileData.biography && (
                      <p className="ipm-bio">{profileData.biography}</p>
                    )}
                  </div>
                </div>
                <div className="ipm-profile-stats">
                  <div className="ipm-stat">
                    <span className="ipm-stat-value">{formatNumber(profileData.postCount)}</span>
                    <span className="ipm-stat-label">gonderi</span>
                  </div>
                  <div className="ipm-stat">
                    <span className="ipm-stat-value">{formatNumber(profileData.followerCount)}</span>
                    <span className="ipm-stat-label">takipci</span>
                  </div>
                  <div className="ipm-stat">
                    <span className="ipm-stat-value">{formatNumber(profileData.followingCount)}</span>
                    <span className="ipm-stat-label">takip</span>
                  </div>
                </div>
              </div>

              {/* İçerik Tipi Seçimi */}
              <div className="ipm-type-section">
                <label>Ne Cekilsin?</label>
                <div className="ipm-type-options">
                  <button
                    className={`ipm-type-btn ${importType === 'posts' ? 'selected' : ''}`}
                    onClick={() => setImportType('posts')}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
                    <span className="ipm-type-label">Sadece Postlar</span>
                    <span className="ipm-type-desc">{profileData.postCount} gonderi</span>
                  </button>
                  <button
                    className={`ipm-type-btn ${importType === 'highlights' ? 'selected' : ''}`}
                    onClick={() => setImportType('highlights')}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
                    <span className="ipm-type-label">Sadece One Cikanlar</span>
                    <span className="ipm-type-desc">Highlight hikayeleri</span>
                  </button>
                  <button
                    className={`ipm-type-btn ${importType === 'both' ? 'selected' : ''}`}
                    onClick={() => setImportType('both')}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/></svg>
                    <span className="ipm-type-label">Ikisi de</span>
                    <span className="ipm-type-desc">Postlar + One Cikanlar</span>
                  </button>
                </div>
              </div>

              {/* Etiket Seçimi */}
              <div className="ipm-tags-section">
                <label>Etiket Secin <span className="ipm-required">*</span></label>
                {tags && tags.length > 0 ? (
                  <div className="ipm-tags-list">
                    {tags.map(tag => (
                      <button
                        key={tag.id}
                        className={`ipm-tag ${selectedTags.includes(tag.id) ? 'selected' : ''}`}
                        onClick={() => toggleTag(tag.id)}
                        style={{
                          backgroundColor: selectedTags.includes(tag.id) ? tag.color : 'transparent',
                          borderColor: tag.color,
                          color: selectedTags.includes(tag.id) ? '#fff' : tag.color
                        }}
                      >
                        {tag.name}
                      </button>
                    ))}
                  </div>
                ) : (
                  <p className="ipm-no-tags">Henuz etiket olusturulmamis. Once etiket ekleyin.</p>
                )}
              </div>

              {/* Platform Ayarları Toggle */}
              <div className="ipm-platform-toggle">
                <button
                  type="button"
                  className={`ipm-platform-toggle-btn ${showPlatformSettings ? 'active' : ''}`}
                  onClick={() => setShowPlatformSettings(!showPlatformSettings)}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/>
                  </svg>
                  <span>Platform Ayarlari {showPlatformSettings ? '(Gizle)' : '(Goster)'}</span>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ transform: showPlatformSettings ? 'rotate(180deg)' : 'none', transition: 'transform 0.3s' }}>
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </button>
                <span className="ipm-platform-hint">
                  Opsiyonel - Icerikler icin Telegram, YouTube, X, TikTok, Instagram ayarlari
                </span>
              </div>

              {/* Platform Settings Panel */}
              {showPlatformSettings && (
                <div className="ipm-platform-settings-wrapper">
                  <PlatformSettingsPanel
                    contentText={contentText}
                    onContentTextChange={setContentText}
                    buttonForm={buttonForm}
                    onButtonFormChange={setButtonForm}
                    youtubeSettings={youtubeSettings}
                    onYoutubeSettingsChange={setYoutubeSettings}
                    xSettings={xSettings}
                    onXSettingsChange={setXSettings}
                    tiktokSettings={tiktokSettings}
                    onTiktokSettingsChange={setTiktokSettings}
                    instagramSettings={instagramSettings}
                    onInstagramSettingsChange={setInstagramSettings}
                    hasVideo={true}
                    hasPhoto={true}
                    bulkMode={true}
                    selectedTagIds={selectedTags}
                    tags={tags}
                    compact={true}
                  />
                </div>
              )}

              <div className="ipm-actions">
                <button onClick={() => { setStep(1); setProfileData(null); }} className="ipm-btn-secondary">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="15 18 9 12 15 6"/></svg>
                  Geri
                </button>
                <button
                  onClick={handleStartImport}
                  disabled={isLoading || selectedTags.length === 0}
                  className="ipm-btn-success"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 2L11 13"/><path d="M22 2L15 22L11 13L2 9L22 2Z"/></svg>
                  Cek ve Indir
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Çekiliyor */}
          {step === 3 && (
            <div className="ipm-step-content ipm-processing">
              <div className="ipm-spinner"></div>

              {fetchPhase === 'posts' && (
                <>
                  <h4>Postlar Taraniyor...</h4>
                  <div className="ipm-fetch-progress">
                    <div className="ipm-progress-bar-wrapper">
                      <div className="ipm-progress-bar" style={{ width: `${getFetchPercent()}%` }} />
                    </div>
                    <div className="ipm-progress-text">
                      <span>%{getFetchPercent()}</span>
                      <span>{fetchedPosts} / {profileData?.postCount || '?'} post</span>
                    </div>
                  </div>
                  <div className="ipm-fetch-stats">
                    <div className="ipm-fetch-stat"><span>Sayfa {fetchedPages}</span></div>
                    <div className="ipm-fetch-stat"><span>{fetchedPosts} post cekildi</span></div>
                  </div>
                </>
              )}

              {fetchPhase === 'highlights' && (
                <>
                  <h4>One Cikanlar Taraniyor...</h4>
                  <div className="ipm-fetch-progress">
                    <div className="ipm-progress-bar-wrapper">
                      <div className="ipm-progress-bar" style={{ width: fetchHighlightTotal > 0 ? `${Math.round((fetchHighlightIndex / fetchHighlightTotal) * 100)}%` : '0%' }} />
                    </div>
                    <div className="ipm-progress-text">
                      <span>{fetchHighlightIndex} / {fetchHighlightTotal} klasor</span>
                      <span>{fetchedHighlightMedia} medya bulundu</span>
                    </div>
                  </div>
                  {fetchHighlightTitle && (
                    <p className="ipm-hint" style={{ marginTop: '12px' }}>
                      "{fetchHighlightTitle}" taraniyor...
                    </p>
                  )}
                </>
              )}

              <p className="ipm-hint" style={{ marginTop: '16px' }}>
                <strong>@{profileData?.username}</strong> profili taraniyor...
              </p>
            </div>
          )}

          {/* Step 4: İndirme İlerlemesi */}
          {step === 4 && (
            <div className="ipm-step-content">
              <div className="ipm-download-header">
                <img
                  src={getProxyImageUrl(profileData?.profilePicUrl)}
                  alt=""
                  className="ipm-mini-pic"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
                <div>
                  <strong>@{profileData?.username}</strong>
                  <span className="ipm-download-subtitle">
                    {totalMedia} medya ({imageCount} fotograf, {videoCount} video)
                    {allPosts.length > 0 && allHighlightItems.length > 0 && (
                      <> - {allPosts.length} post + {allHighlightItems.length} story</>
                    )}
                  </span>
                </div>
              </div>

              {/* İndirme Fazı */}
              {!isCompleted && downloadPhase && (
                <div className="ipm-download-phase">
                  {downloadPhase === 'posts' ? 'Postlar indiriliyor...' : 'One cikanlar indiriliyor...'}
                </div>
              )}

              <div className="ipm-progress-section">
                <div className="ipm-progress-bar-wrapper">
                  <div
                    className={`ipm-progress-bar ${isCompleted ? 'completed' : ''}`}
                    style={{ width: `${getProgressPercent()}%` }}
                  />
                </div>
                <div className="ipm-progress-text">
                  <span>%{getProgressPercent()}</span>
                  <span>{downloadedMedia} / {totalMedia} medya</span>
                </div>
              </div>

              <div className="ipm-download-stats">
                <div className="ipm-dstat">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/></svg>
                  <span className="ipm-dstat-label">Islenen</span>
                  <span className="ipm-dstat-value">{currentPostIndex} / {totalItems}</span>
                </div>
                <div className="ipm-dstat">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#4ecb71" strokeWidth="2"><polyline points="20 6 9 17 4 12"/></svg>
                  <span className="ipm-dstat-label">Kaydedilen</span>
                  <span className="ipm-dstat-value ipm-success-text">{savedCount}</span>
                </div>
                <div className="ipm-dstat">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#e85d4a" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>
                  <span className="ipm-dstat-label">Hata</span>
                  <span className="ipm-dstat-value ipm-error-text">{errorCount}</span>
                </div>
              </div>

              {!isCompleted && currentPostIndex > 0 && (
                <div className="ipm-current-post">
                  <div className="ipm-current-dot"></div>
                  <span>
                    {downloadPhase === 'posts'
                      ? `Post ${currentPostIndex}/${allPosts.length} indiriliyor`
                      : `Story ${currentPostIndex - allPosts.length}/${allHighlightItems.length} indiriliyor`
                    }
                  </span>
                </div>
              )}

              {isCompleted && (
                <div className="ipm-completed-section">
                  <div className="ipm-completed-icon">
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#4ecb71" strokeWidth="2"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
                  </div>
                  <h4>Islem Tamamlandi!</h4>
                  <p>{savedCount} medya basariyla kutupahneye eklendi.</p>
                  {errorCount > 0 && (
                    <p className="ipm-error-note">{errorCount} medya indirilemedi.</p>
                  )}
                </div>
              )}

              <div className="ipm-actions" style={{ justifyContent: isCompleted ? 'center' : 'space-between' }}>
                {!isCompleted ? (
                  <button onClick={() => { cancelRef.current = true; }} className="ipm-btn-danger">
                    Iptal Et
                  </button>
                ) : (
                  <button onClick={() => { resetModal(); onClose(); if (onSuccess) onSuccess(); }} className="ipm-btn-primary">
                    Tamam, Kapat
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default InstagramProfileModal;